
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/layout/Layout";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Grid2x2, Layers, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchClips } from "@/utils/supabase/dataFetching";
import { formatDuration } from "@/utils/dataUtils/formatters";
import { SEO } from "@/components/seo/SEO";
import { getPageUrl } from "@/utils/seo/urlUtils";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";

// Clip card component
const ClipCard = ({ clip }) => (
  <Link to={`/clip/${clip.id}`} key={clip.id} className="group">
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-md h-full">
      <div className="relative">
        <AspectRatio ratio={16/9}>
          <img 
            src={clip.thumbnail} 
            alt={clip.title} 
            className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
            {formatDuration(clip.duration || 0)}
          </div>
          <div className="absolute bottom-3 right-3 bg-white/90 dark:bg-black/70 text-primary rounded-full p-2 shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
            <ArrowRight className="h-4 w-4" />
          </div>
        </AspectRatio>
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg line-clamp-1">{clip.title}</CardTitle>
        <CardDescription className="line-clamp-1">
          <span className="inline-flex items-center">
            <Layers className="h-4 w-4 mr-1.5" /> {clip.playlists?.name || clip.playlist_name || 'Unknown'}
          </span>
        </CardDescription>
      </CardHeader>
    </Card>
  </Link>
);

// Skeleton component for loading state
const ClipSkeleton = () => (
  <Card className="overflow-hidden">
    <div className="aspect-video w-full relative overflow-hidden">
      <Skeleton className="h-full w-full" />
    </div>
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-3/4 mb-2" />
      <Skeleton className="h-4 w-1/2" />
    </CardHeader>
  </Card>
);

const AllClips = () => {
  const { t } = useLanguage();
  
  // Use React Query for better caching and state management
  const clipsQuery = useQuery({
    queryKey: ['all-clips'],
    queryFn: async () => {
      try {
        // Fetch clips with a reasonable limit
        const data = await fetchClips(undefined, 100);
        
        // Shuffle the clips for variety
        const shuffledData = [...data].sort(() => Math.random() - 0.5);
        console.log("Clips data (shuffled):", shuffledData.length);
        
        return shuffledData;
      } catch (error) {
        console.error("Error loading clips:", error);
        toast.error("Failed to load clips");
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  const isLoading = clipsQuery.isLoading;
  const clips = clipsQuery.data || [];
  
  return (
    <Layout>
      <SEO 
        title={t('clips.allClips')} 
        description={t('clips.description')}
        keywords="recut, video clips, licensing, creative use, content creation"
        ogUrl={getPageUrl("/clips")}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center">
            <Grid2x2 className="h-6 w-6 mr-2" />
            {t('clips.allClips')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('clips.description')}
          </p>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, i) => (
              <ClipSkeleton key={i} />
            ))}
          </div>
        ) : clips.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('common.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {clips.map((clip) => (
              <ClipCard key={clip.id} clip={clip} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllClips;
